import React, { useEffect, useContext, useState, ChangeEvent } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { GetMonitoringScorecardResponseViewModel } from "../ViewModels/GetMonitoringScorecardResponse";
import { APIContext } from "../../Contexts/APIContext";
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import FlaggedIssueViewModel from '../ViewModels/FlaggedIssueViewModel';
import AddFlaggedIssueViewModel from '../ViewModels/AddFlaggedIssueViewModel';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FlaggedIssuesModalRow from './FlaggedIssuesModalRow';

type Props = {
    open: boolean;
    onClose: (refresh: boolean) => void;
    scorecard: GetMonitoringScorecardResponseViewModel | null;
    callRefId: string;
}

export default function FlaggedIssuesModal({ open, onClose, scorecard, callRefId }: Props) {
    const { get, post } = useContext(APIContext);
    const [flaggedIssues, setFlaggedIssues] = useState<FlaggedIssueViewModel[]>([]);
    const [newFlaggedIssue, setNewFlaggedIssue] = useState(new AddFlaggedIssueViewModel(0, 0, ""));
    const [openAddIssue, setOpenAddIssue] = useState(false);

    useEffect(() => {
        if (callRefId !== "" && scorecard !== null && scorecard.templateId !== 0 && open) getData();
    }, [callRefId, scorecard, open]);

    useEffect(() => {
        if (callRefId !== "" && scorecard !== null && scorecard.templateId !== 0) setNewFlaggedIssue(new AddFlaggedIssueViewModel(scorecard.templateId, scorecard.existingId!, callRefId));
    }, [callRefId, scorecard]);

    const getData = () => {
        get('MonitoringScorecard', `GetFlaggedIssues?templateId=${scorecard!.templateId}&callRefId=${callRefId}`, (data) => {
            setFlaggedIssues(data);
        });
    }

    const addFlaggedIssue = () => {
        post('MonitoringScorecard', `AddFlaggedIssue`, newFlaggedIssue, (data) => {
            setOpenAddIssue(false);
            getData();
            setNewFlaggedIssue(new AddFlaggedIssueViewModel(scorecard!.templateId, scorecard!.existingId!, callRefId));
        });
    }

    const toggleAddIssue = (refresh: boolean) => {
        if (refresh) getData();
        setOpenAddIssue((prev) => !prev);
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setNewFlaggedIssue({
            ...newFlaggedIssue,
            [e.target.name]: e.target.value
        });
    }

    return (
        <Dialog
            open={open}
            onClose={() => onClose(true)}
            maxWidth="xl"
            fullWidth
        >
            <DialogTitle>Flagged Issues</DialogTitle>
            <DialogContent>
                <Grid container justifyContent="center">
                    <Grid size={12}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Section</TableCell>
                                    <TableCell>Question</TableCell>
                                    <TableCell>Notes</TableCell>
                                    <TableCell>Username</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {flaggedIssues && flaggedIssues.length > 0 && flaggedIssues.map((flaggedIssue, index) => {
                                    return (
                                        <FlaggedIssuesModalRow key={index} flaggedIssue={flaggedIssue} getData={getData} />
                                    );
                                })}
                                <TableRow>
                                    <TableCell align="center" colSpan={6}>
                                        {openAddIssue ?
                                            <Grid container spacing={2}>
                                                <Grid size={6}>
                                                    <TextField
                                                        name="sectionId"
                                                        select
                                                        onChange={handleChange}
                                                        value={newFlaggedIssue.sectionId}
                                                        variant="outlined"
                                                        size="small"
                                                        label="Section"
                                                        fullWidth
                                                    >
                                                        {scorecard!.sections.map((section) => {
                                                            return (
                                                                <MenuItem key={section.id} value={section.id}>{section.name}</MenuItem>
                                                            );
                                                        })}
                                                    </TextField>
                                                </Grid>
                                                <Grid size={6}>
                                                    <TextField
                                                        name="questionId"
                                                        select
                                                        onChange={handleChange}
                                                        value={newFlaggedIssue.questionId}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        label="Question"
                                                        disabled={newFlaggedIssue.sectionId === 0}
                                                    >
                                                        {newFlaggedIssue.sectionId !== 0 && scorecard!.sections.filter(f => f.id === newFlaggedIssue.sectionId)[0].questions.map((question) => {
                                                            return (
                                                                <MenuItem key={question.id} value={question.id}>{question.text}</MenuItem>
                                                            );
                                                        })}
                                                    </TextField>
                                                </Grid>
                                                <Grid size={12}>
                                                    <TextField
                                                        name="notes"
                                                        multiline
                                                        rows={4}
                                                        onChange={handleChange}
                                                        value={newFlaggedIssue.notes}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        label="Notes"
                                                    />
                                                </Grid>
                                                <Grid size={6}>
                                                    <Button variant="contained" size="small" fullWidth onClick={() => { setOpenAddIssue(false); setNewFlaggedIssue(new AddFlaggedIssueViewModel()); }}>Cancel</Button>
                                                </Grid>
                                                <Grid size={6}>
                                                    <Button variant="contained" size="small" fullWidth onClick={addFlaggedIssue}>Submit</Button>
                                                </Grid>
                                            </Grid>
                                            :
                                            <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={() => toggleAddIssue(false)}>Add New Issue</Button>
                                        }
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Grid>
                </Grid>
                <DialogActions>
                    <Button onClick={() => onClose(true)}>Close</Button>
                </DialogActions>
            </DialogContent>
        </Dialog>
    )
}